import React, { useState, useEffect } from "react";

import { Grid, MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { useAppState } from "../../../../context/StateContext";
import { Destination } from "../../../../models/destination";
import { DifferentLocation as LocationAddress } from "../../../../models/location";
import { Material } from "../../../../models/material";
import { RoomJob } from "../../../../models/sales";
import { ServiceBooking } from "../../../../models/service.booking";
import MaterialsService from "../../../../services/materials.service";
import { Status, Mode } from "../../../../util/enums";
import { getValueFromEachDestination } from "../../../../util/getDestinations";
import { isAllied } from "../../../../util/isAllied";
import { getPriceStringValid } from "../../../../util/prices";
import { Input, Select } from "../../../Form";
import Modal from "../../Modal";
import { NotificationToast } from "../../Toast/NotificationToast";
import useStyles from "./styles";
import useYupValidation from "Hooks/useYupValidation";

type Props = {
  modalAddItemIsOpen: boolean;
  inventory?: RoomJob[];
  jobId?: number;
  onClose?: () => void;
  onSave: (materials: Material) => void;
  destinations: Destination[];
  locations: LocationAddress[];
  materialList?: ServiceBooking[];
};

const ModalAddItem = (props: Props) => {
  const {
    modalAddItemIsOpen,
    inventory,
    jobId,
    onClose,
    onSave,
    destinations,
    locations,
    materialList,
  } = props;

  const { currentUser } = useAppState();

  const [material, setMaterial] = useState<Material>({} as Material);

  const [serviceBookings, setServiceBookings] = useState<ServiceBooking[]>(materialList || []);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { t: translate } = useTranslation();

  const classes = useStyles();

  useQuery(
    ["materials"],
    () =>
      MaterialsService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        orderBy: JSON.stringify(["name"]),
      }),
    {
      enabled: !!currentUser?.companyId && !materialList,
      onSuccess: (response) => {
        if (response.data && response.data.length > 0) setServiceBookings(response.data);
      },
    }
  );

  useEffect(() => {
    setMaterial({ quantity: 1, uuid: uuid() } as Material);
  }, [modalAddItemIsOpen]);

  const onChangeMaterial = (data: Material) => {
    setMaterial((state) => ({ ...(state || ({} as Material)), ...data, volume: 0, jobId }));
  };

  const schema = yup.object().shape({
    quantity: yup
      .number()
      .min(1, translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    roomId: yup.number().required(translate("thisFieldIsRequired")),
    hasServiceBooking: yup.boolean().isTrue(translate("thisFieldIsRequired")),
    destinationId: yup.number().required(translate("thisFieldIsRequired")),
  });

  const validation = () => {
    try {
      schema.validateSync(
        {
          roomId: material?.roomId,
          quantity: material?.quantity,
          hasServiceBooking: !!material?.serviceBooking?.id,
          destinationId:
            material?.destinationId ||
            material?.destinationResidenceId ||
            material?.modeDestinationId,
        },
        { abortEarly: false }
      );

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const validateForm = (): boolean => {
    const hasValidMaterial = validation();

    if (!hasValidMaterial) {
      NotificationToast.warning(translate("fillAllFieldsToSave"));

      return false;
    }

    return true;
  };

  const renderRooms = () => {
    if (inventory && inventory?.length > 0) {
      return inventory?.map((roomJob) => {
        return (
          <MenuItem key={roomJob?.id} value={roomJob?.id}>
            {roomJob?.name}
          </MenuItem>
        );
      });
    }

    return null;
  };

  const getFilteredServiceBookings = () => {
    const roomId = material?.roomId;

    if (!roomId || !serviceBookings || serviceBookings.length === 0) {
      return serviceBookings;
    }

    const filtered = serviceBookings.filter((serviceBooking) =>
      serviceBooking?.rooms?.some((room) => room?.id === roomId)
    );

    return filtered.length > 0 ? filtered : serviceBookings;
  };

  const renderDescriptions = () => {
    const bookingsToRender = getFilteredServiceBookings();

    if (bookingsToRender && bookingsToRender.length > 0) {
      return bookingsToRender?.map((serviceBooking) => {
        return (
          <MenuItem key={serviceBooking?.id} value={serviceBooking?.id}>
            {serviceBooking?.name}
          </MenuItem>
        );
      });
    }

    return null;
  };

  const getDestinationByMaterialSelected = (materialSelected?: Material) => {
    if (materialSelected && materialSelected?.destination && materialSelected?.destinationId) {
      return materialSelected?.destination?.name;
    }

    if (
      materialSelected &&
      materialSelected?.modeDestination &&
      materialSelected?.modeDestinationId
    ) {
      return materialSelected?.modeDestination?.name;
    }

    if (
      materialSelected &&
      materialSelected?.destinationResidence &&
      materialSelected?.destinationResidenceId
    ) {
      return `Residence: ${materialSelected?.destinationResidence?.longAddress}`;
    }

    return "";
  };

  const getMaterialByDestination = (destination?: Destination, materialSelected?: Material) => {
    if (destination) {
      let mode = "";

      if (destination?.name?.toLocaleLowerCase().includes("freight")) {
        const modeSplit = destination?.name?.split(" ");

        if (modeSplit && modeSplit.length > 0) {
          const [destinationName] = modeSplit;

          mode = destinationName;
        }
      }

      if (isAllied(currentUser?.company)) {
        if (destination?.destinationResidenceId) {
          return Mode.Road;
        }
        if (mode) return mode;
      } else if (mode) return mode;
    }

    return materialSelected && materialSelected?.mode ? materialSelected?.mode : undefined;
  };

  const [localPrice, setLocalPrice] = useState<string>("");
  const [materialFocused, setMaterialFocused] = useState<string | number>();

  const renderRowForm = (hideLabel: boolean, material?: Material) => {
    return (
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        style={{ marginTop: "10px" }}
        spacing={2}
        data-cy={`material-form`}
      >
        <Grid item container justifyContent="center" alignItems="flex-start" xs={2} md={2} lg={2}>
          <Input
            name="quantity"
            required
            error={fieldHasError("quantity")}
            errorMessage={getErrorByField("quantity")}
            label={hideLabel ? undefined : translate("quantity")}
            onChange={(e) => {
              const valueNumber = Number(e.target.value.replace(/[^0-9]/g, ""));

              onChangeMaterial({
                ...material,
                quantity: valueNumber,
              } as Material);
            }}
            value={material?.quantity ?? 1}
            data-cy={`quantity`}
          />
        </Grid>
        <Grid item xs={10} md={10} lg={10}>
          <Select
            data-cy={`description`}
            name="description"
            required
            error={fieldHasError("hasServiceBooking")}
            errorMessage={getErrorByField("hasServiceBooking")}
            label={hideLabel ? undefined : translate("description")}
            value={material?.serviceBooking?.id}
            onChange={(event) =>
              onChangeMaterial({
                ...material,
                serviceBooking: serviceBookings?.find(
                  (serviceBooking) => serviceBooking.id === Number(event.target.value)
                ),
              } as Material)
            }
            displayEmpty
            placeholder={translate("selectDescription")}
          >
            {renderDescriptions()}
          </Select>
        </Grid>
        <Grid item xs={3} md={3} lg={3}>
          <Select
            name="room"
            required
            error={fieldHasError("roomId")}
            errorMessage={getErrorByField("roomId")}
            label={hideLabel ? undefined : translate("room")}
            value={material?.roomId}
            onChange={(event) => {
              const room = inventory?.find((roomJob) => roomJob.id === Number(event.target.value));

              onChangeMaterial({
                ...material,
                room: {
                  id: room?.id,
                  name: room?.name,
                  itemsJob: room?.itemsJob,
                  pictures: room?.pictures,
                  roomPictures: room?.roomPictures,
                  companyId: room?.companyId,
                },
                roomId: Number(event.target.value),
              } as Material);
            }}
            data-cy={`room`}
            displayEmpty
            placeholder={translate("selectRoom")}
          >
            {renderRooms()}
          </Select>
        </Grid>
        <Grid item xs={4} md={4} lg={4}>
          <Select
            name="destination"
            required
            error={fieldHasError("destinationId")}
            errorMessage={getErrorByField("destinationId")}
            data-cy={`destination`}
            label={hideLabel ? undefined : translate("destination")}
            value={getDestinationByMaterialSelected(material)}
            onChange={(event) => {
              if (!material) return;
              const destination = destinations?.find(
                (destination) => destination.name === event.target.value
              );

              onChangeMaterial({
                ...material,
                destination: getValueFromEachDestination(destination)?.destination,
                destinationId: getValueFromEachDestination(destination)?.destination?.id || null,

                destinationResidence: getValueFromEachDestination(destination, locations)
                  ?.destinationResidence,
                destinationResidenceId:
                  getValueFromEachDestination(destination, locations)?.destinationResidence?.id ||
                  null,

                modeDestination: getValueFromEachDestination(destination)?.modeDestination,
                modeDestinationId:
                  getValueFromEachDestination(destination)?.modeDestination?.id || null,
                mode: getMaterialByDestination(destination, material),
              } as Material);
            }}
            placeholder={translate("chooseADestination")}
          >
            {destinations &&
              destinations?.map((destination, index: number) => (
                <MenuItem
                  key={destination.name || index}
                  value={destination.name}
                  data-cy={`material-destination-${destination.name}`}
                >
                  {destination.name}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <Grid item xs={5} md={5} lg={5}>
          <Input
            value={
              materialFocused && [material?.id, material?.uuid].includes(materialFocused)
                ? localPrice
                : material?.price?.toString()
            }
            type="text"
            data-cy={`price`}
            label={translate("price")}
            onFocus={() => {
              setLocalPrice(String(material?.price || ""));
              setMaterialFocused(material?.id || material?.uuid);
            }}
            onChange={(event) => {
              const decimalSeparator = currentUser?.company?.currency?.decimalSeparator ?? ".";
              const decimalPoints = currentUser?.company?.currency?.decimalPoints ?? 2;

              const withPointer = getPriceStringValid(event.target.value, decimalSeparator);

              if (withPointer.includes(decimalSeparator)) {
                const verifyPrice = withPointer.split(decimalSeparator);

                if (verifyPrice[1].length <= Number(decimalPoints)) {
                  setLocalPrice(withPointer);
                }
              } else setLocalPrice(withPointer);
            }}
            onBlur={() => {
              onChangeMaterial({
                ...material,
                price:
                  currentUser?.company?.currency?.decimalSeparator === ","
                    ? Number(localPrice.replace(",", ".")) || 0
                    : Number(localPrice) || 0,
              } as Material);
              setLocalPrice("");
              setMaterialFocused(undefined);
            }}
            placeholder="0.00"
            leftIcon={"$"}
          />
        </Grid>
      </Grid>
    );
  };

  const renderInputs = () => {
    if (material) {
      return renderRowForm(false, material);
    }

    return renderRowForm(false);
  };

  return (
    <Modal
      open={modalAddItemIsOpen}
      handleClose={() => {
        onClose?.();
        setErrors([]);
      }}
      title={translate("addMaterial")}
      showActionButtons
      actionButtonProps={{
        dataCySave: "modal-add-item-save-button",
        textSave: translate("add"),
        onSave: () => (validateForm() ? onSave({ ...material, uuid: uuid() }) : null),
      }}
    >
      <div className={classes.gridContainer}>
        <Grid container direction="column" justifyContent="space-between" alignItems="center">
          {renderInputs()}
        </Grid>
        {/* TODO Felipe change this button to standardizate with figma */}
        {/* <Button
          customColor={Color.SECONDARY}
          data-cy="button-add-another"
          className={classes.button}
          onClick={addMaterial}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
          >
            <AddCircleOutlineIcon fontSize="small" />
            <span>
              {translate("total")}
            </span>
          </Stack>
        </Button> */}
      </div>
    </Modal>
  );
};

export default ModalAddItem;
